import { createContext, ReactElement, useCallback, useContext, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { visitPaths } from '~libs.desktop.shared.paths';

import { mapDateFilterParamToDateFilter } from './AppointmentsFilterChips/DateChipFilter.utils';
import { mapPractitionerIdsFilterToPractitionerIds } from './AppointmentsFilterPanel/fields/DoctorsFilter/DoctorsFilter.utils';
import { mapVisitAndAppointmentStatusFilterParamToStatuses } from './AppointmentsFilterPanel/fields/VisitAndAppointmentStatusField/VisitAndAppointmentStatusField.utils';
import { mapVisitEncounterModeFilterParamToEncounterMode } from './AppointmentsFilterPanel/fields/VisitEncounterModeField/VisitEncounterModeField.utils';
import { mapAppointmentConfirmationStatusFilterParamToAppointmentConfirmationStatus } from './AppointmentsFilterPanel/fields/AppointmentConfirmationStatusField/AppointmentConfirmationStatusField.utils';
import { mapPatientPresenceFilterParamToPatientPresence } from './AppointmentsFilterPanel/fields/PatientPresenceField/PatientPresenceField.utils';
import { mapPatientGroupFilterToPatientGroupIds } from './AppointmentsFilterPanel/fields/PatientGroupField/PatientGroupField.utils';

type AppointmentsFilterSearchParam =
   | typeof visitPaths.APPOINTMENTS_DATE_SEARCH_PARAM
   | typeof visitPaths.APPOINTMENTS_PATIENT_SEARCH_PARAM
   | typeof visitPaths.APPOINTMENTS_PRACTITIONER_SEARCH_PARAM
   | typeof visitPaths.VISIT_AND_APPOINTMENT_STATUS_SEARCH_PARAM
   | typeof visitPaths.VISIT_ENCOUNTER_MODE_SEARCH_PARAM
   | typeof visitPaths.APPOINTMENT_CONFIRMATION_STATUS_SEARCH_PARAM
   | typeof visitPaths.PATIENT_PRESENCE_SEARCH_PARAM
   | typeof visitPaths.PATIENT_GROUP_SEARCH_PARAM;

export type AppointmentsFilter =
   | 'appointmentConfirmationStatus'
   | 'date'
   | 'patientPresence'
   | 'patient'
   | 'practitioner'
   | 'visitAndAppointmentStatus'
   | 'visitEncounterMode'
   | 'patientGroup';

const appointmentsFilterToSearchParamMap: Record<AppointmentsFilter, AppointmentsFilterSearchParam> = {
   appointmentConfirmationStatus: visitPaths.APPOINTMENT_CONFIRMATION_STATUS_SEARCH_PARAM,
   patientPresence: visitPaths.PATIENT_PRESENCE_SEARCH_PARAM,
   patient: visitPaths.APPOINTMENTS_PATIENT_SEARCH_PARAM,
   practitioner: visitPaths.APPOINTMENTS_PRACTITIONER_SEARCH_PARAM,
   visitAndAppointmentStatus: visitPaths.VISIT_AND_APPOINTMENT_STATUS_SEARCH_PARAM,
   visitEncounterMode: visitPaths.VISIT_ENCOUNTER_MODE_SEARCH_PARAM,
   patientGroup: visitPaths.PATIENT_GROUP_SEARCH_PARAM,
   date: visitPaths.APPOINTMENTS_DATE_SEARCH_PARAM,
};

const mapFilterParamToFilterValue = {
   appointmentConfirmationStatus: mapAppointmentConfirmationStatusFilterParamToAppointmentConfirmationStatus,
   patientPresence: mapPatientPresenceFilterParamToPatientPresence,
   patient: (patientId: string | null) => patientId,
   practitioner: mapPractitionerIdsFilterToPractitionerIds,
   visitAndAppointmentStatus: mapVisitAndAppointmentStatusFilterParamToStatuses,
   visitEncounterMode: mapVisitEncounterModeFilterParamToEncounterMode,
   patientGroup: mapPatientGroupFilterToPatientGroupIds,
   date: mapDateFilterParamToDateFilter,
} as const;

export type AppointmentsFiltersContext = {
   getSearchParams: () => URLSearchParams;
   replaceSearchParams: (searchParams: URLSearchParams) => void;
   getFilter: <K extends keyof typeof mapFilterParamToFilterValue>(
      name: K
   ) => ReturnType<typeof mapFilterParamToFilterValue[K]>;
};

const appointmentsFiltersContext = createContext<AppointmentsFiltersContext | null>(null);

export const useAppointmentsFiltersContext = (): AppointmentsFiltersContext => {
   const context = useContext(appointmentsFiltersContext);

   if (!context) {
      throw new Error('useAppointmentsFiltersContext must be used within an AppointmentsFiltersContextProvider');
   }

   return context;
};

const { Provider } = appointmentsFiltersContext;

type AppointmentsFiltersContextProviderProps = {
   children: React.ReactNode;
};

export const AppointmentsFiltersContextProvider = ({
   children,
}: AppointmentsFiltersContextProviderProps): ReactElement => {
   const location = useLocation();
   const history = useHistory();

   const getSearchParams = useCallback(() => new URLSearchParams(location.search), [location.search]);

   const replaceSearchParams = useCallback(
      (searchParams: URLSearchParams) => {
         history.replace({
            search: searchParams.toString(),
            state: location.state,
         });
      },
      [history, location.state]
   );

   const memoizedFilters = useRef<Map<AppointmentsFilter, { rawFilter: string | null; filter: unknown }>>(new Map());

   const getFilter = useCallback(
      (name: AppointmentsFilter) => {
         const searchParam = appointmentsFilterToSearchParamMap[name];
         const rawFilter = getSearchParams().get(searchParam);
         const mapper = mapFilterParamToFilterValue[name];

         const memoized = memoizedFilters.current.get(name);

         if (memoized && memoized.rawFilter === rawFilter) {
            return memoized.filter;
         }

         const mappedValue = mapper(rawFilter);
         memoizedFilters.current.set(name, { rawFilter: rawFilter, filter: mappedValue });

         return mappedValue;
      },
      [getSearchParams]
   ) as AppointmentsFiltersContext['getFilter'];

   const value = useMemo<AppointmentsFiltersContext>(
      () => ({
         getSearchParams: getSearchParams,
         replaceSearchParams: replaceSearchParams,
         getFilter: getFilter,
      }),
      [getSearchParams, replaceSearchParams, getFilter]
   );

   return <Provider value={value}>{children}</Provider>;
};
